import { toRelativeUrl } from "@okta/okta-auth-js";
import { LoginCallback, useOktaAuth } from "@okta/okta-react";
import React, { ReactElement, ReactNode, useContext, useEffect } from "react";
import {
  createBrowserRouter,
  createMemoryRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from "react-router";
import Layout from "../components/Layout/Layout";
import { DELEGATE_LOGON_PATH, LOGOUT_PATH } from "../constant";
import { ConfigContext } from "../context/ConfigContext";
import AppContainer from "./AppContainer/AppContainer";
import ProvidersFederation from "./ProvidersFederation/ProvidersFederation";
import { OrderContextProvider } from "../context/OrderContext/OrderContext";
import { RBACAccessContext } from "../context/RBACAccessContext/RBACAccessContext";
import { SharedState } from "../context/SharedStateContext/SharedStateContext";
import { FullStory } from "../FullStory";
import CorporateViewWrapper from "./components/CorporateViewWrapper/CorporateViewWrapper";
import JDRoleLandingViewWrapper from "./components/JDRoleLandingViewWrapper/JDRoleLandingViewWrapper";
import RedirectToNewUrlWrapper from "./components/RedirectToNewUrlWrapper/RedirectToNewUrlWrapper";
import AlertsAndNotificationCenter from "../pages/alertAndNotifications/list/LazyAlertsAndNotificationCenter";
import CreateBroadcastMessage from "../pages/broadcastMessages/CreateBroadcastMessage/LazyCreateBroadcastMessage";
import ViewAllBroadcastMessages from "../pages/broadcastMessages/list/LazyBroadcastMessageList";
import { AllClaimsProvider } from "../pages/claims/allClaimsV2/context/allClaimsContext/AllClaimsContext";
import AllClaimsV2 from "../pages/claims/allClaimsV2/LazyAllClaims";
import ClaimDetailsV2 from "../pages/claims/claimDetailsV2/LazyClaimDetails";
import CreateClaimV2 from "../pages/claims/createClaimsV2/LazyCreateClaimsV2";
import CreditStatus from "../pages/creditStatus/LazyCreditStatus";
import D2DOrders from "../pages/d2d/D2DOrders/D2DOrders";
import D2DRequests from "../pages/d2d/D2DRequests/D2DRequests";
import MyLocatorEntry from "../pages/d2d/MyLocatorEntry/MyLocatorEntry";
import MyLocatorInventory from "../pages/d2d/MyLocatorInventory/MyLocatorInventory";
import OtherDealerInventory from "../pages/d2d/OtherDealerInventory/OtherDealerInventory";
import Dashboard from "../pages/dashboard/LazyDashboard";
import DelegateLogon from "../pages/delegateLogon/LazyDelegateLogon";
import Eligibilities from "../pages/eligibilities/LazyEligibilities";
import FeatureToggleV2 from "../pages/featureToggles/list/LazyFeatureToggle";
import HandlingUnitDetails from "../pages/handlingUnits/HandlingUnitDetails/LazyHandlingUnitDetails";
import HandlingUnitDetailsV2 from "../pages/handlingUnits/handlingUnitDetailsV2/LazyHandlingUnitDetails";
import { HandlingUnitListProvider } from "../pages/handlingUnits/list/context/handlingUnitListContext/HandlingUnitListContext";
import HandlingUnitList from "../pages/handlingUnits/list/LazyHandlingUnitList";
import ViewAllHandlingUnits from "../pages/handlingUnits/ViewAllHUs/LazyViewAllHandlingUnits";
import AllInvoices from "../pages/invoices/AllInvoices/LazyAllInvoices";
import ConsolidateInvoices from "../pages/invoices/ConsolidateInvoices/LazyConsolidateInvoices";
import InvoiceDetails from "../pages/invoices/InvoiceDetails/InvoiceDetails";
import LazyInvoiceDetails from "../pages/invoices/InvoiceDetailsV2/LazyInvoiceDetails";
import { InvoicesListProvider } from "../pages/invoices/list/context/InvoicesListContext";
import Invoices from "../pages/invoices/list/Invoices";
import Logout from "../pages/logout/LazyLogout";
import AllOrdersV2 from "../pages/orders/AllOrders/LazyAllOrders";
import EstimatedCostSummary from "../pages/orders/EstimatedCostSummary/EstimatedCostSummary";
import OrdersByLineItemV2 from "../pages/orders/OrdersByLineItem/LazyOrdersByLineItem";
import SelectAccountForOrder from "../pages/orders/SelectAccountForOrder";
import Shell from "../pages/orders/Shell/LazyOrderShell";
import SummaryV2 from "../pages/orders/Summary/LazySummary";
import PartsInfo from "../pages/parts/PartInfo/LazyPartsInfo";
import Preferences from "../pages/preferences/LazyPreferences";
import PromotionDetailsV2 from "../pages/promotionsV2/PromotionDetails/LazyPromotionDetails";
import PromotionsList from "../pages/promotionsV2/PromotionsList/LazyPromotionsList";
import AllCoreReturns from "../pages/returns/allCoreReturnsList/LazyAllCoreReturns";
import { AllCoreReturnsProvider } from "../pages/returns/context/allCoreReturnsContext/AllCoreReturnsContext";
import CreateCoreReturns from "../pages/returns/create/coreReturns/LazyCreateCoreReturns";
import CreateReturns from "../pages/returns/create/dailyReturns/LazyCreateReturns";
import SurplusReturns from "../pages/returns/create/surplusReturns/LazySurplusReturns";
import CoreReturnDetails from "../pages/returns/details/coreReturns/LazyCoreReturnDetails";
import ReturnDetailsPage from "../pages/returns/details/dailyReturns/LazyReturnDetailsPage";
import SurplusReturnDetailsPage from "../pages/returns/details/surplusReturns/LazySurplusReturnDetailsPage";
import Returns from "../pages/returns/LazyReturns";
import TrackingDetails from "../pages/tracking/LazyTrackingDetails";
import {
  isAllInvoicesEnabled,
  isConsolidatedInvoicesEnabled,
} from "../utils/showInvoices";

interface RouterProps {
  routerType?: "browser" | "memory";
  initialEntries?: string[];
}

function Router({ routerType = "browser", initialEntries = [] }: RouterProps) {
  const {
    ENABLE_HANDLING_UNITS,
    ENABLE_CORE_ELIGIBILITIES,
    ENABLE_INVOICES_CONSOLIDATED,
    ENABLE_ALL_INVOICES,
    ENABLE_INVOICE_DETAILS,
    ENABLE_PREFERENCES,
    ENABLE_FEATURE_TOGGLES,
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
    ENABLE_FEATURE_TOGGLE_SERVICE_INTEGRATION,
    ENABLE_CREDIT_STATUS,
    ENABLE_RBAC_INTEGRATION,
    ENABLE_CORE_RETURNS,
    ENABLE_HANDLING_UNITS_V2,
    ENABLE_INVOICES_V2,
    ENABLE_REMOVE_DASHBOARD_TAB,
    ENABLE_ACCOUNT_SELECTION_ON_ORDER_CREATE,
    ENABLE_DEALER_PATH_FOR_CLAIMS,
    ENABLE_D2D,
    ENABLE_INVOICE_PERSIST_FILTER_ON_SESSION,
    ENABLE_HU_PERSIST_FILTER_ON_SESSION,
    ENABLE_DELEGATE_LOGIN,
  } = useContext(ConfigContext);

  const { authState, oktaAuth } = useOktaAuth();
  const { isRBACEnabled } = useContext(RBACAccessContext);
  const DEFAULT_ROUTE_WRAPPERS = [
    ...(isRBACEnabled ? [RedirectToNewUrlWrapper] : []),
    ...(isRBACEnabled ? [CorporateViewWrapper] : []),
    ...(isRBACEnabled ? [JDRoleLandingViewWrapper] : []),
  ];

  const DELEGATE_LOGON_ROUTE_WRAPPERS = [
    ...(isRBACEnabled ? [RedirectToNewUrlWrapper] : []),
    ...(isRBACEnabled ? [CorporateViewWrapper] : []),
  ];

  const wrapRouteComponent = (
    children: ReactNode,
    wrappers = DEFAULT_ROUTE_WRAPPERS
  ) =>
    wrappers.reduce(
      (
        wrappedComponents: ReactNode,
        Wrapper: React.FC<{
          children: ReactNode;
        }>
      ) => <Wrapper>{wrappedComponents}</Wrapper>,
      children
    );

  useEffect(() => {
    if (!authState) return;

    if (
      window.location.pathname === "/implicit/callback" ||
      window.location.pathname === LOGOUT_PATH
    )
      return;

    if (!authState.isAuthenticated) {
      const originalUri = toRelativeUrl(
        window.location.href,
        window.location.origin
      );
      oktaAuth.setOriginalUri(originalUri);
      oktaAuth.signInWithRedirect();
    }
  }, [authState]);

  useEffect(() => {
    if (authState?.idToken?.claims && authState.accessToken?.claims) {
      const name = String(authState.idToken.claims.name ?? "");
      const sub = String(authState.accessToken.claims.userID ?? "");

      const normalizedSub = String(sub ?? "").toLowerCase();

      window.walkme_user_id = normalizedSub;
      FullStory.initialise({ name, sub: normalizedSub });
    }
  }, [
    authState?.idToken?.claims?.name,
    authState?.accessToken?.claims?.userID,
  ]);

  const routes = (
    <>
      <Route path="/implicit/callback" Component={LoginCallback} />
      {ENABLE_RBAC_INTEGRATION && (
        <Route
          path={LOGOUT_PATH}
          element={
            <AppContainer>
              <Logout />
            </AppContainer>
          }
        />
      )}
      <Route
        element={
          <ProvidersFederation>
            <SharedState>
              <AppContainer>
                <Layout>
                  <Outlet />
                </Layout>
              </AppContainer>
            </SharedState>
          </ProvidersFederation>
        }
      >
        {ENABLE_DELEGATE_LOGIN && (
          <Route
            path={DELEGATE_LOGON_PATH}
            element={wrapRouteComponent(
              <DelegateLogon />,
              DELEGATE_LOGON_ROUTE_WRAPPERS
            )}
          />
        )}
        {!ENABLE_RBAC_INTEGRATION &&
          ENABLE_INVOICES_CONSOLIDATED &&
          isConsolidatedInvoicesEnabled() && (
            <Route
              path="invoices/download"
              element={wrapRouteComponent(<ConsolidateInvoices />)}
            />
          )}
        {((ENABLE_RBAC_INTEGRATION && ENABLE_ALL_INVOICES) ||
          (ENABLE_ALL_INVOICES && isAllInvoicesEnabled())) && (
          <>
            {ENABLE_INVOICES_V2 ? (
              <Route
                path="invoices"
                element={wrapRouteComponent(<Invoices />)}
              />
            ) : (
              <Route
                path="invoices"
                element={wrapRouteComponent(<AllInvoices />)}
              />
            )}
            {ENABLE_INVOICE_DETAILS && (
              <Route
                path="invoices/:invoiceNumber"
                element={
                  ENABLE_INVOICES_V2
                    ? wrapRouteComponent(<LazyInvoiceDetails />)
                    : wrapRouteComponent(<InvoiceDetails />)
                }
              />
            )}
          </>
        )}
        {ENABLE_HANDLING_UNITS && (
          <>
            <Route
              path="/dealer/:dealerAccountId/handlingUnits"
              element={
                ENABLE_HANDLING_UNITS_V2
                  ? wrapRouteComponent(<HandlingUnitList />)
                  : wrapRouteComponent(<ViewAllHandlingUnits />)
              }
            />
            <Route
              path="/dealer/:dealerAccountId/handlingUnits/:huNumber"
              element={
                ENABLE_HANDLING_UNITS_V2
                  ? wrapRouteComponent(<HandlingUnitDetailsV2 />)
                  : wrapRouteComponent(<HandlingUnitDetails />)
              }
            />
            <Route
              path="/dealer/:dealerAccountId/tracking/:huNumber"
              element={wrapRouteComponent(<TrackingDetails />)}
            />
            {ENABLE_RBAC_INTEGRATION && (
              <>
                <Route
                  path="/handlingUnits"
                  element={
                    ENABLE_HANDLING_UNITS_V2
                      ? wrapRouteComponent(<HandlingUnitList />)
                      : wrapRouteComponent(<ViewAllHandlingUnits />)
                  }
                />
                <Route
                  path="/handlingUnits/:huNumber"
                  element={
                    ENABLE_HANDLING_UNITS_V2
                      ? wrapRouteComponent(<HandlingUnitDetailsV2 />)
                      : wrapRouteComponent(<HandlingUnitDetails />)
                  }
                />
                <Route
                  path="/tracking/:huNumber"
                  element={wrapRouteComponent(<TrackingDetails />)}
                />
              </>
            )}
          </>
        )}
        <Route
          path="/dealer/:dealerAccountId/promotions"
          element={wrapRouteComponent(<PromotionsList />)}
        />
        <Route
          path="/dealer/:dealerAccountId/promotions/:promotionId"
          element={wrapRouteComponent(<PromotionDetailsV2 />)}
        />
        {ENABLE_RBAC_INTEGRATION && (
          <>
            <Route
              path="/promotions/:promotionId"
              element={wrapRouteComponent(<PromotionDetailsV2 />)}
            />
            <Route
              path="/promotions"
              element={wrapRouteComponent(<PromotionsList />)}
            />
          </>
        )}
        <>
          <Route
            path="/dealer/:dealerAccountId/parts"
            element={wrapRouteComponent(<PartsInfo />)}
          />
          <Route
            path="/dealer/:dealerAccountId/parts/:partNumber"
            element={wrapRouteComponent(<PartsInfo />)}
          />
          {ENABLE_RBAC_INTEGRATION && (
            <>
              <Route
                path="/parts"
                element={wrapRouteComponent(<PartsInfo />)}
              />
              <Route
                path="/parts/:partNumber"
                element={wrapRouteComponent(<PartsInfo />)}
              />
            </>
          )}
        </>

        <>
          <Route
            path="/dealer/:dealerAccountId/orders/create/:orderType"
            element={wrapRouteComponent(
              <OrderContextProvider>
                <Shell />
              </OrderContextProvider>
            )}
          />
          <Route
            path="/dealer/:dealerAccountId/orders/create/:orderType/:orderNumber"
            element={wrapRouteComponent(
              <OrderContextProvider>
                <Shell />
              </OrderContextProvider>
            )}
          />
          <Route
            path="/dealer/:dealerAccountId/orders/:orderNumber"
            element={wrapRouteComponent(<SummaryV2 />)}
          />
          <Route
            path="/dealer/:dealerAccountId/orders"
            element={wrapRouteComponent(<AllOrdersV2 />)}
          />
          <Route
            path="/dealer/:dealerAccountId/orders/line-items"
            element={wrapRouteComponent(<OrdersByLineItemV2 />)}
          />
          {ENABLE_RBAC_INTEGRATION && (
            <>
              <Route
                path="/orders/create/:orderType"
                element={wrapRouteComponent(
                  <OrderContextProvider>
                    <Shell />
                  </OrderContextProvider>
                )}
              />
              <Route
                path="/orders/create/:orderType/:orderNumber"
                element={wrapRouteComponent(
                  <OrderContextProvider>
                    <Shell />
                  </OrderContextProvider>
                )}
              />
              <Route
                path="/orders/:orderNumber"
                element={wrapRouteComponent(<SummaryV2 />)}
              />
              <Route
                path="/orders"
                element={wrapRouteComponent(<AllOrdersV2 />)}
              />
              <Route
                path="/orders/line-items"
                element={wrapRouteComponent(<OrdersByLineItemV2 />)}
              />
              {ENABLE_ACCOUNT_SELECTION_ON_ORDER_CREATE && (
                <Route
                  path="/orders/:orderType/select-account"
                  element={wrapRouteComponent(
                    <OrderContextProvider>
                      <SelectAccountForOrder />
                    </OrderContextProvider>
                  )}
                />
              )}
            </>
          )}
        </>
        <Route
          path="/dealer/:dealerAccountId/returns"
          element={wrapRouteComponent(<Returns />)}
        />
        <Route
          path="/dealer/:dealerAccountId/returns/create"
          element={wrapRouteComponent(<CreateReturns />)}
        />
        <Route
          path="/dealer/:dealerAccountId/returns/createSurplus"
          element={wrapRouteComponent(<SurplusReturns />)}
        />
        <Route
          path="/dealer/:dealerAccountId/returns/:returnId"
          element={wrapRouteComponent(<ReturnDetailsPage />)}
        />
        <Route
          path="/dealer/:dealerAccountId/returns/surplus/:returnId"
          element={wrapRouteComponent(<SurplusReturnDetailsPage />)}
        />
        {ENABLE_CORE_ELIGIBILITIES && (
          <Route
            path="/dealer/:dealerAccountId/eligibilities"
            element={wrapRouteComponent(<Eligibilities />)}
          />
        )}
        <Route path="/returns" element={wrapRouteComponent(<Returns />)} />
        <Route
          path="/returns/create"
          element={wrapRouteComponent(<CreateReturns />)}
        />
        <Route
          path="/returns/createSurplus"
          element={wrapRouteComponent(<SurplusReturns />)}
        />
        <Route
          path="/returns/:returnId"
          element={wrapRouteComponent(<ReturnDetailsPage />)}
        />
        <Route
          path="/returns/surplus/:returnId"
          element={wrapRouteComponent(<SurplusReturnDetailsPage />)}
        />
        {ENABLE_CORE_ELIGIBILITIES && (
          <Route
            path="/eligibilities"
            element={wrapRouteComponent(<Eligibilities />)}
          />
        )}
        {ENABLE_PREFERENCES && (
          <>
            <Route
              path="/dealer/:dealerAccountId/preferences"
              element={wrapRouteComponent(
                <Preferences />,
                ENABLE_RBAC_INTEGRATION
                  ? [RedirectToNewUrlWrapper, JDRoleLandingViewWrapper]
                  : []
              )}
            />
            {ENABLE_RBAC_INTEGRATION && (
              <Route
                path="/preferences"
                element={wrapRouteComponent(<Preferences />, [
                  RedirectToNewUrlWrapper,
                ])}
              />
            )}
          </>
        )}
        {(ENABLE_FEATURE_TOGGLES ||
          (ENABLE_FEATURE_TOGGLE_CONTEXT_V2 &&
            ENABLE_FEATURE_TOGGLE_SERVICE_INTEGRATION)) && (
          <>
            <Route
              path="/dealer/:dealerAccountId/feature-toggles"
              element={wrapRouteComponent(<FeatureToggleV2 />)}
            />
            {ENABLE_RBAC_INTEGRATION && (
              <Route
                path="/feature-toggles"
                element={wrapRouteComponent(<FeatureToggleV2 />)}
              />
            )}
          </>
        )}
        {ENABLE_CREDIT_STATUS && (
          <>
            <Route
              path="/dealer/:dealerAccountId/credit-status"
              element={wrapRouteComponent(<CreditStatus />)}
            />
            {ENABLE_RBAC_INTEGRATION && (
              <Route
                path="/credit-status"
                element={wrapRouteComponent(<CreditStatus />)}
              />
            )}
          </>
        )}
        <>
          <Route
            path={
              ENABLE_REMOVE_DASHBOARD_TAB
                ? "/dealer/:dealerAccountId/home"
                : "/dealer/:dealerAccountId/my-dashboard"
            }
            element={wrapRouteComponent(<Dashboard />)}
          />
          {ENABLE_RBAC_INTEGRATION && (
            <>
              <Route
                path={ENABLE_REMOVE_DASHBOARD_TAB ? "/home" : "/my-dashboard"}
                element={wrapRouteComponent(<Dashboard />, [
                  RedirectToNewUrlWrapper,
                ])}
              />
              <Route
                path="*"
                element={
                  <Navigate
                    to={ENABLE_REMOVE_DASHBOARD_TAB ? "/home" : "/my-dashboard"}
                    replace
                  />
                }
              />
            </>
          )}
        </>
        {ENABLE_RBAC_INTEGRATION && (
          <>
            <Route
              path="/dealer/:dealerAccountId/broadcast-messages"
              element={wrapRouteComponent(<ViewAllBroadcastMessages />)}
            />
            <Route
              path="/broadcast-messages"
              element={wrapRouteComponent(<ViewAllBroadcastMessages />)}
            />
            <Route
              path="/dealer/:dealerAccountId/create/broadcast-message"
              element={wrapRouteComponent(<CreateBroadcastMessage />)}
            />
            <Route
              path="/create/broadcast-message"
              element={wrapRouteComponent(<CreateBroadcastMessage />)}
            />
            <Route
              path="/edit/broadcast-message"
              element={wrapRouteComponent(<CreateBroadcastMessage />)}
            />
          </>
        )}
        <Route
          path="/alerts-notifications-center"
          element={wrapRouteComponent(<AlertsAndNotificationCenter />)}
        />
        {ENABLE_D2D && (
          <>
            <Route
              path="/dealer/:dealerAccountId/my-locator-entry"
              element={wrapRouteComponent(<MyLocatorEntry />)}
            />
            <Route
              path="/my-locator-entry"
              element={wrapRouteComponent(<MyLocatorEntry />)}
            />
            <Route
              path="/dealer/:dealerAccountId/my-locator-inventory"
              element={wrapRouteComponent(<MyLocatorInventory />)}
            />
            <Route
              path="/my-locator-inventory"
              element={wrapRouteComponent(<MyLocatorInventory />)}
            />
            <Route
              path="/dealer/:dealerAccountId/other-dealer-inventory"
              element={wrapRouteComponent(<OtherDealerInventory />)}
            />
            <Route
              path="/other-dealer-inventory"
              element={wrapRouteComponent(<OtherDealerInventory />)}
            />
            <Route
              path="/dealer/:dealerAccountId/d2d-orders"
              element={wrapRouteComponent(<D2DOrders />)}
            />
            <Route
              path="/d2d-orders"
              element={<Navigate to="/d2d-orders-buy" replace />}
            />
            <Route
              path="/d2d-orders-buy"
              element={wrapRouteComponent(<D2DOrders />)}
            />
            <Route
              path="/dealer/:dealerAccountId/d2d-requests"
              element={wrapRouteComponent(<D2DRequests />)}
            />
            <Route
              path="/d2d-requests"
              element={<Navigate to="/d2d-orders-sell" replace />}
            />
            <Route
              path="/d2d-orders-sell"
              element={wrapRouteComponent(<D2DRequests />)}
            />
          </>
        )}
      </Route>

      <Route
        element={
          <ProvidersFederation>
            <SharedState>
              <AppContainer>
                <Layout>
                  <AllCoreReturnsProvider>
                    <Outlet />
                  </AllCoreReturnsProvider>
                </Layout>
              </AppContainer>
            </SharedState>
          </ProvidersFederation>
        }
      >
        {ENABLE_CORE_RETURNS && (
          <>
            <Route
              path="/dealer/:dealerAccountId/returns/core/:returnId"
              element={wrapRouteComponent(<CoreReturnDetails />)}
            />
            <Route
              path="/dealer/:dealerAccountId/returns/createCoreReturns"
              element={wrapRouteComponent(<CreateCoreReturns />)}
            />
            <Route
              path="/dealer/:dealerAccountId/returns/core"
              element={wrapRouteComponent(<AllCoreReturns />)}
            />
            <Route
              path="/returns/core/:returnId"
              element={wrapRouteComponent(<CoreReturnDetails />)}
            />
            <Route
              path="/returns/createCoreReturns"
              element={wrapRouteComponent(<CreateCoreReturns />)}
            />
            <Route
              path="/returns/core"
              element={wrapRouteComponent(<AllCoreReturns />)}
            />
          </>
        )}
      </Route>

      <Route
        element={
          <ProvidersFederation>
            <SharedState>
              <AppContainer>
                <Layout>
                  <AllClaimsProvider>
                    <Outlet />
                  </AllClaimsProvider>
                </Layout>
              </AppContainer>
            </SharedState>
          </ProvidersFederation>
        }
      >
        {ENABLE_DEALER_PATH_FOR_CLAIMS && (
          <>
            <Route
              path="/dealer/:dealerAccountId/claim/create"
              element={wrapRouteComponent(<CreateClaimV2 />)}
            />
            <Route
              path="/dealer/:dealerAccountId/claims"
              element={wrapRouteComponent(<AllClaimsV2 />)}
            />
            <Route
              path="/dealer/:dealerAccountId/claims/:claimId"
              element={wrapRouteComponent(<ClaimDetailsV2 />)}
            />
            <Route
              path="/dealer/:dealerAccountId/claim/create-by-hu"
              element={wrapRouteComponent(<CreateClaimV2 />)}
            />
          </>
        )}
        <>
          <Route
            path="claim/create"
            element={wrapRouteComponent(<CreateClaimV2 />)}
          />
          <Route path="claims" element={wrapRouteComponent(<AllClaimsV2 />)} />
          <Route
            path="claims/:claimId"
            element={wrapRouteComponent(<ClaimDetailsV2 />)}
          />
          <Route
            path="claim/create-by-hu"
            element={wrapRouteComponent(<CreateClaimV2 />)}
          />
        </>
      </Route>

      <Route
        element={
          <ProvidersFederation>
            <AppContainer>
              <Outlet />
            </AppContainer>
          </ProvidersFederation>
        }
      >
        <Route
          path="/dealer/:dealerAccountId/orders/:orderType/:orderNumber/estimated-cost-summary"
          element={wrapRouteComponent(<EstimatedCostSummary />)}
        />
        {ENABLE_RBAC_INTEGRATION && (
          <Route
            path="/orders/:orderType/:orderNumber/estimated-cost-summary"
            element={wrapRouteComponent(<EstimatedCostSummary />)}
          />
        )}
      </Route>
    </>
  );

  const routeObjects = createRoutesFromElements(routes);

  const router =
    routerType === "browser"
      ? createBrowserRouter(routeObjects)
      : createMemoryRouter(routeObjects, {
          initialEntries,
        });

  let routerElement: ReactElement = <RouterProvider router={router} />;

  if (ENABLE_INVOICE_PERSIST_FILTER_ON_SESSION) {
    routerElement = (
      <InvoicesListProvider>{routerElement}</InvoicesListProvider>
    );
  }

  if (ENABLE_HU_PERSIST_FILTER_ON_SESSION) {
    routerElement = (
      <HandlingUnitListProvider>{routerElement}</HandlingUnitListProvider>
    );
  }

  return routerElement;
}

export default Router;
